<template lang="pug">
.pms
  v-menu(
    v-model='open'
    :disabled='readonly'
    :close-on-content-click='false'
    :top='openUp'
    offset-y
    :nudge-bottom='openUp ? 0 : 4'
    :nudge-top='openUp ? 4 : 0'
    :min-width='menuWidth'
    :max-width='menuWidth'
    content-class='pms-menu'
  )
    template(v-slot:activator)
      .pms-box(
        ref='box'
        tabindex='0'
        role='combobox'
        :aria-expanded='String(open)'
        :aria-label='placeholder'
        :aria-readonly='String(readonly)'
        :class='{ "pms-box--open": open, "pms-box--readonly": readonly }'
        @click='onBoxClick'
        @keydown='onBoxKeydown'
      )
        v-img.pms-icon(:src='iconSrc' height='20' width='20' max-width='20' contain)
        v-chip.pms-chip(v-for='item in value' :key='item.id' :title='item.name')
          span.pms-chip-text {{ item.name }}
        span.pms-placeholder(v-if='!value.length') {{ placeholder }}
    .pms-panel
      .pms-search
        v-icon(size='16' color='#757575') mdi-magnify
        input.pms-q(
          ref='q'
          type='text'
          autocomplete='off'
          :value='query'
          :placeholder='searchPlaceholder'
          :aria-label='searchPlaceholder'
          @input='query = $event.target.value'
          @keydown='onQueryKeydown'
        )
        button.pms-qx(v-if='query' type='button' :aria-label='$t("KB_AND_BOT_PERMISSION_SETTING.CLEAR_SEARCH")' @click.stop='clearQuery')
          v-icon(size='14') mdi-close
      ul.pms-opts(
        ref='opts'
        role='listbox'
        aria-multiselectable='true'
        :style='{ height: optsHeight + "px", maxHeight: optsHeight + "px" }'
      )
        template(v-if='filteredItems.length')
          li.pms-all(
            role='option'
            tabindex='-1'
            :aria-selected='String(allSelected)'
            @click='toggleAll'
            @keydown='onOptionKeydown($event, null)'
          )
            span.pms-ck(:class='{ "pms-ck--on": allSelected || someSelected }')
              v-icon(v-if='allSelected' size='12' color='white') mdi-check
              v-icon(v-else-if='someSelected' size='12' color='white') mdi-minus
            | {{ trimmedQuery ? $t('KB_AND_BOT_PERMISSION_SETTING.SELECT_ALL_RESULTS') : $t('GENERAL.SELECTALL') }}
          li.pms-opt(
            v-for='item in filteredItems'
            :key='item.id'
            role='option'
            tabindex='-1'
            :aria-selected='String(isSelected(item))'
            @click='toggleItem(item)'
            @keydown='onOptionKeydown($event, item)'
          )
            span.pms-ck(:class='{ "pms-ck--on": isSelected(item) }')
              v-icon(v-if='isSelected(item)' size='12' color='white') mdi-check
            span.pms-t(:title='item.name')
              template(v-for='(part, i) in highlightParts(item.name)')
                mark(v-if='part.hit' :key='i') {{ part.text }}
                span(v-else :key='i') {{ part.text }}
        li.pms-empty(v-else aria-disabled='true') {{ emptyText }}
</template>

<script>
import Vue from "vue";
import userIcon from "@/assets/images/user_icon.png";
import groupIcon from "@/assets/images/group_icon.png";

// 權限設定用的多選元件（使用者／群組），選單內附搜尋框。
// v-model 為 [{ id, name, ... }]，與原本 v-select return-object 的資料格式相同。
export default Vue.extend({
  name: "permissionMultiSelect",
  props: {
    value: { type: Array, default: () => [] },
    items: { type: Array, default: () => [] },
    kind: {
      type: String,
      default: "user",
      validator: (v) => ["user", "group"].includes(v),
    },
    placeholder: { type: String, default: "" },
    readonly: { type: Boolean, default: false },
  },
  data() {
    return {
      open: false,
      query: "",
      openUp: false,
      menuWidth: 0,
      optsHeight: 360,
    };
  },
  computed: {
    iconSrc() {
      return this.kind === "user" ? userIcon : groupIcon;
    },
    trimmedQuery() {
      return this.query.trim();
    },
    filteredItems() {
      const q = this.trimmedQuery.toLowerCase();
      if (!q) return this.items;
      return this.items.filter((i) => String(i.name).toLowerCase().includes(q));
    },
    selectedIdSet() {
      return new Set(this.value.map((v) => v.id));
    },
    selectedInFilteredCount() {
      return this.filteredItems.filter((i) => this.selectedIdSet.has(i.id)).length;
    },
    allSelected() {
      return this.filteredItems.length > 0 && this.selectedInFilteredCount === this.filteredItems.length;
    },
    someSelected() {
      return this.selectedInFilteredCount > 0 && !this.allSelected;
    },
    searchPlaceholder() {
      return this.$t(this.kind === "user" ? "KB_AND_BOT_PERMISSION_SETTING.SEARCH_USER" : "KB_AND_BOT_PERMISSION_SETTING.SEARCH_GROUP");
    },
    emptyText() {
      return this.$t(this.kind === "user" ? "KB_AND_BOT_PERMISSION_SETTING.NO_MATCH_USER" : "KB_AND_BOT_PERMISSION_SETTING.NO_MATCH_GROUP", { q: this.trimmedQuery });
    },
  },
  watch: {
    open(val) {
      if (val) this.$nextTick(() => this.focusSearch());
    },
    readonly(val) {
      if (val) this.open = false;
    },
  },
  methods: {
    isSelected(item) {
      return this.selectedIdSet.has(item.id);
    },
    highlightParts(name) {
      const text = String(name);
      const q = this.trimmedQuery;
      if (!q) return [{ text, hit: false }];
      const i = text.toLowerCase().indexOf(q.toLowerCase());
      if (i < 0) return [{ text, hit: false }];
      return [
        { text: text.slice(0, i), hit: false },
        { text: text.slice(i, i + q.length), hit: true },
        { text: text.slice(i + q.length), hit: false },
      ].filter((p) => p.text);
    },
    emitValue(list) {
      this.$emit("input", list);
      this.$emit("change", list);
    },
    toggleItem(item) {
      this.emitValue(
        this.isSelected(item) ? this.value.filter((v) => v.id !== item.id) : [...this.value, item]
      );
    },
    // 只對目前（搜尋後）顯示的項目全選／取消，其餘已選項目保留
    toggleAll() {
      if (!this.filteredItems.length) return;
      if (this.allSelected) {
        const ids = new Set(this.filteredItems.map((i) => i.id));
        this.emitValue(this.value.filter((v) => !ids.has(v.id)));
      } else {
        const toAdd = this.filteredItems.filter((i) => !this.selectedIdSet.has(i.id));
        this.emitValue([...this.value, ...toAdd]);
      }
    },
    openMenu() {
      if (this.readonly || this.open) return;
      const box = this.$refs.box;
      const rect = box.getBoundingClientRect();
      const below = window.innerHeight - rect.bottom - 16;
      const above = rect.top - 16;
      const searchH = 45;
      const want = 360;
      // 下方空間不足且上方較大時，往上展開
      this.openUp = below < want + searchH && above > below;
      this.optsHeight = Math.max(120, Math.min(want, (this.openUp ? above : below) - searchH));
      this.menuWidth = box.offsetWidth;
      this.query = "";
      this.open = true;
    },
    closeMenu(focusBox) {
      this.open = false;
      if (focusBox && this.$refs.box) this.$refs.box.focus();
    },
    onBoxClick() {
      this.open ? this.closeMenu(false) : this.openMenu();
    },
    onBoxKeydown(e) {
      if (["Enter", " ", "ArrowDown"].includes(e.key)) {
        e.preventDefault();
        this.openMenu();
      }
    },
    // v-menu 內容顯示前 focus 會失敗（display:none），失敗時短暫重試
    focusSearch(retries = 15) {
      const q = this.$refs.q;
      if (q) q.focus();
      if (this.open && document.activeElement !== q && retries > 0) {
        setTimeout(() => this.focusSearch(retries - 1), 30);
      }
    },
    clearQuery() {
      this.query = "";
      if (this.$refs.q) this.$refs.q.focus();
    },
    focusOption(index) {
      const rows = this.$refs.opts ? this.$refs.opts.querySelectorAll("li[tabindex]") : [];
      if (rows[index]) rows[index].focus();
    },
    onQueryKeydown(e) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        this.focusOption(0);
      } else if (e.key === "Enter") {
        // 加入／移除第一筆符合的項目
        e.preventDefault();
        if (this.filteredItems.length) this.toggleItem(this.filteredItems[0]);
      } else if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation(); // 避免連 dialog 一起被 Esc 關掉
        this.closeMenu(true);
      } else if (e.key === "Tab") {
        this.closeMenu(false);
      }
    },
    onOptionKeydown(e, item) {
      const rows = Array.from(this.$refs.opts.querySelectorAll("li[tabindex]"));
      const idx = rows.indexOf(e.currentTarget);
      if (e.key === "ArrowDown") {
        e.preventDefault();
        this.focusOption(Math.min(idx + 1, rows.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        idx <= 0 ? this.$refs.q.focus() : this.focusOption(idx - 1);
      } else if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        item ? this.toggleItem(item) : this.toggleAll();
      } else if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        this.closeMenu(true);
      } else if (e.key === "Tab") {
        this.closeMenu(false);
      }
    },
  },
});
</script>

<style scoped>
.pms {
  position: relative;
}
.pms-box {
  position: relative;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  min-height: 48px;
  max-height: 90px;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 6px 44px 6px 10px;
  border: 1px solid #747474d0;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  box-sizing: border-box;
}
.pms-box::-webkit-scrollbar {
  width: 6px;
}
.pms-box::-webkit-scrollbar-thumb {
  background: rgba(139, 139, 139, 0.308);
  border-radius: 10px;
}
/* 下拉箭頭（畫在捲動區外，不會跟著捲動） */
.pms::after {
  content: "";
  position: absolute;
  right: 18px;
  top: 50%;
  margin-top: -3px;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 7px solid #757575;
  pointer-events: none;
}
.pms-box:focus-visible,
.pms-box--open {
  border-color: #14c6de;
  outline: none;
}
.pms-box--readonly {
  cursor: default;
}
.pms-icon {
  flex: none;
}
.pms-placeholder {
  font-size: 15px;
  color: #9e9e9e;
}
.pms-chip {
  max-width: 100px;
  color: #666666 !important;
  font-size: 14px !important;
  font-weight: 400 !important;
}
.pms-chip-text {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ---- 下拉內容（v-menu 會把內容搬到 body，但 scoped 屬性仍保留在內容元素上） ---- */
.pms-panel {
  display: flex;
  flex-direction: column;
  background: #fff;
  overflow: hidden;
}
.pms-search {
  display: flex;
  align-items: center;
  flex: none;
  gap: 8px;
  height: 44px;
  padding: 0 10px 0 16px;
  border-bottom: 1px solid #e0e0e0;
}
.pms-q {
  flex: 1;
  min-width: 0;
  height: 32px;
  border: none;
  outline: none;
  background: transparent;
  color: #333;
  font-size: 14px;
  letter-spacing: 0.3px;
}
.pms-q::placeholder {
  color: #9e9e9e;
}
.pms-qx {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 50%;
  background: none;
  cursor: pointer;
}
.pms-qx:hover {
  background: #f0f0f0;
}
.pms-opts {
  margin: 0;
  padding: 4px 0;
  list-style: none;
  overflow-y: auto;
}
.pms-opts li {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 40px;
  padding: 0 8px 0 16px;
  color: #555;
  font-size: 14px;
  letter-spacing: 0.3px;
  cursor: pointer;
}
.pms-opts li:hover,
.pms-opts li:focus {
  background: #f4f4f4;
  outline: none;
}
.pms-opts li.pms-all {
  margin-bottom: 4px;
  border-bottom: 1px solid #e0e0e0;
  color: #333;
}
.pms-opts li.pms-empty {
  justify-content: center;
  padding: 0 16px;
  color: #9e9e9e;
  font-size: 13px;
  cursor: default;
}
.pms-opts li.pms-empty:hover {
  background: none;
}
.pms-t {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pms-t mark {
  padding: 0;
  background: #fff3b0;
  color: inherit;
}
.pms-ck {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border: 1.5px solid #666;
  border-radius: 2px;
  background: #fff;
}
.pms-ck--on {
  border-color: #14c6de;
  background: #14c6de;
}
</style>

<style>
/* v-menu 內容容器本身不帶 scoped 屬性，需用全域 class */
.pms-menu {
  border-radius: 5px;
  background: #fff;
  overflow: hidden;
}
</style>
